import { satoshisToDollars, dollarsToSatoshis } from "../../utils";
import { productStatusToServiceStatus, serviceStatusToProductStatus } from "../product";
import { serviceInventoryMediaTypeToInventoryMediaType, inventoryMediaTypeToServiceInventoryMediaType, serviceInventoryVideoTypeToInventoryVideoType, inventoryVideoTypeToServiceInventoryVideoType } from "../inventories";
import { stationGroupPricingRowFromService, stationGroupPricingRowToService } from "./shared";
/**
 * Converts service product detail to UI format.
 * Unflattens: service flat structure → UI nested billingInfo.
 * Transforms status, CPM (satoshis to dollars).
 * @param item - Service product from detail endpoint
 * @returns UI-formatted product with billing info
 */
export const pricingProductFromService = (item) => {
    const hasBillingInfo = item.media_type != null;
    return {
        id: item.prod_id,
        name: item.name,
        status: serviceStatusToProductStatus(item.status),
        defaultRetailCpm: item.default_retail_cpm != null ? satoshisToDollars(item.default_retail_cpm) : undefined,
        billingInfo: hasBillingInfo
            ? {
                mediaType: serviceInventoryMediaTypeToInventoryMediaType(item.media_type),
                videoType: serviceInventoryVideoTypeToInventoryVideoType(item.product_type),
                stationGroups: item.station_groups?.map(stationGroupPricingRowFromService)
            }
            : undefined
    };
};
/**
 * Converts service product list item to UI format.
 * Reuses the detail transform; list items never carry station groups.
 * @param item - Service product from list endpoint
 * @returns UI-formatted product (billing info without station groups)
 */
export const pricingProductListItemFromService = (item) => {
    const { id, ...rest } = item;
    return pricingProductFromService({ ...rest, prod_id: id });
};
/**
 * Converts UI product to service format.
 * Flattens: UI nested billingInfo → service flat structure.
 * Transforms status, CPM (dollars to satoshis).
 * @param item - UI product (nested billingInfo)
 * @returns Service-formatted product
 */
export const pricingProductToService = (item) => {
    return {
        prod_id: item.id,
        name: item.name,
        status: productStatusToServiceStatus(item.status),
        default_retail_cpm: item.defaultRetailCpm != null ? dollarsToSatoshis(item.defaultRetailCpm) : undefined,
        media_type: item.billingInfo
            ? inventoryMediaTypeToServiceInventoryMediaType(item.billingInfo.mediaType)
            : undefined,
        product_type: item.billingInfo
            ? inventoryVideoTypeToServiceInventoryVideoType(item.billingInfo.videoType)
            : undefined,
        station_groups: item.billingInfo?.stationGroups?.map(stationGroupPricingRowToService)
    };
};
/**
 * Converts product to service update default CPM request.
 * Extracts defaultRetailCpm and transforms from dollars to satoshis.
 * @param product - Full product pricing list item
 * @returns Service-formatted update request
 */
export const toServiceUpdateProductDefaultCpmRequest = (product) => {
    return {
        default_retail_cpm: product.defaultRetailCpm != null ? dollarsToSatoshis(product.defaultRetailCpm) : undefined
    };
};
/**
 * Converts product to service upsert station group CPM request.
 * Extracts valid station groups (with id and cpm) and transforms CPMs from dollars to satoshis.
 * @param product - Full product pricing list item
 * @returns Service-formatted upsert request
 */
export const toServiceUpsertStationGroupCpmRequestFromProduct = (product) => {
    const productId = product.id;
    const stationGroups = product.billingInfo?.stationGroups ?? [];
    const validStationGroups = stationGroups.filter((sg) => !!sg.stationGroupId && sg.cpm != null);
    return {
        upserts: validStationGroups.map((sg) => ({
            prod_id: productId,
            station_group_id: sg.stationGroupId,
            cpm: dollarsToSatoshis(sg.cpm)
        }))
    };
};
//# sourceMappingURL=productPricing.js.map